import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core"
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import MoveUpIcon from "@mui/icons-material/MoveUp"
import {
    Box,
    Divider,
    IconButton,
    List,
    ListItem,
    Popover,
    Stack,
    Typography,
} from "@mui/material"
import { GridColDef } from "@mui/x-data-grid"
import {
    MouseEvent,
    ReactElement,
    useCallback,
    useEffect,
    useState,
} from "react"

import AppButton from "../../buttons/appButton"

// Draggable Column Item Component for vertical list
interface DraggableColumnItemProps {
    column: GridColDef
    isDragging?: boolean
}

const DraggableColumnItem = ({
    column,
    isDragging,
}: DraggableColumnItemProps): ReactElement => {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id: column.field })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    }

    return (
        <ListItem
            ref={setNodeRef}
            style={style}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...attributes}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...listeners}
            sx={{
                display: "flex",
                alignItems: "center",
                padding: "8px 16px",
                cursor: "grab",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                marginBottom: 0.5,
                backgroundColor: "background.paper",
                "&:active": {
                    cursor: "grabbing",
                },
                "&:hover": {
                    backgroundColor: "action.hover",
                },
            }}
        >
            <DragIndicatorIcon
                sx={{
                    marginRight: 1,
                    color: "text.secondary",
                    fontSize: "1.2rem",
                }}
            />
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
                {column.headerName || column.field}
            </Typography>
        </ListItem>
    )
}

// Column Reorder Popover Component
interface ColumnReorderPopoverProps {
    columns: GridColDef[]
    onColumnOrderChange: (newOrder: string[]) => void
    anchorEl: HTMLElement | null
    open: boolean
    onClose: () => void
}

const ColumnReorderPopover = ({
    columns,
    onColumnOrderChange,
    anchorEl,
    open,
    onClose,
}: ColumnReorderPopoverProps): ReactElement => {
    const [items, setItems] = useState(
        columns.map((col) => ({ id: col.field, column: col }))
    )
    const [hasChanges, setHasChanges] = useState(false)

    // Update items when columns change
    useEffect(() => {
        setItems(columns.map((col) => ({ id: col.field, column: col })))
        setHasChanges(false)
    }, [columns])

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event

        if (over && active.id !== over.id) {
            setItems((currentItems) => {
                const oldIndex = currentItems.findIndex(
                    (item) => item.id === active.id
                )
                const newIndex = currentItems.findIndex(
                    (item) => item.id === over.id
                )

                const newItems = arrayMove(currentItems, oldIndex, newIndex)
                setHasChanges(true)

                return newItems
            })
        }
    }, [])

    const handleApplyChanges = useCallback(() => {
        if (hasChanges) {
            const newOrder = items.map((item) => item.id)
            onColumnOrderChange(newOrder)
            setHasChanges(false)
        }
        onClose()
    }, [items, hasChanges, onColumnOrderChange, onClose])

    const handleCancel = useCallback(() => {
        if (hasChanges) {
            // Reset items to original order
            setItems(columns.map((col) => ({ id: col.field, column: col })))
            setHasChanges(false)
        }
        onClose()
    }, [columns, hasChanges, onClose])

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "left",
            }}
            PaperProps={{
                sx: {
                    minWidth: 280,
                    maxWidth: 320,
                    maxHeight: 450,
                    padding: 2,
                },
            }}
        >
            <Stack spacing={1}>
                <Typography variant="body1" color="text.secondary">
                    Drag to reorder columns
                </Typography>
                <Divider />

                <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={items.map((item) => item.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            <List sx={{ padding: 0 }}>
                                {items.map((item) => (
                                    <DraggableColumnItem
                                        key={item.id}
                                        column={item.column}
                                    />
                                ))}
                            </List>
                        </SortableContext>
                    </DndContext>
                </Box>

                <Divider />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <AppButton
                        variant="text"
                        size="small"
                        onClick={handleCancel}
                    >
                        Cancel
                    </AppButton>
                    <AppButton
                        variant="contained"
                        size="small"
                        onClick={handleApplyChanges}
                        disabled={!hasChanges}
                    >
                        Done
                    </AppButton>
                </Box>
            </Stack>
        </Popover>
    )
}

// Column Reorder Interface Component for Toolbar
interface ColumnReorderInterfaceProps {
    columns: GridColDef[]
    onColumnOrderChange: (newOrder: string[]) => void
}

const ColumnReorderInterface = ({
    columns,
    onColumnOrderChange,
}: ColumnReorderInterfaceProps): ReactElement => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const open = Boolean(anchorEl)

    const handleClick = useCallback((event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }, [])

    const handleClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    return (
        <>
            <IconButton
                onClick={handleClick}
                size="small"
                sx={{
                    color: "primary.main",
                    "&:hover": {
                        backgroundColor: "action.hover",
                        borderRadius: 1,
                    },
                }}
            >
                <MoveUpIcon fontSize="small" />
                <Typography sx={{ marginLeft: 1, fontSize: "0.95rem" }}>
                    REORDER
                </Typography>
            </IconButton>

            <ColumnReorderPopover
                columns={columns}
                onColumnOrderChange={onColumnOrderChange}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            />
        </>
    )
}

export default ColumnReorderInterface
export type { ColumnReorderInterfaceProps }
